import { Injectable, inject } from '@angular/core';
import { Query } from '@datorama/akita';
import { GiftModel, PropertyInformationModel } from '../../cmg-1003/models';
import { Cmg1003PropertyStore } from './property.store';
import { DirtyChecker2 } from '../core/dirty-checker-2';

@Injectable({ providedIn: 'root' })
export class Cmg1003PropertyQuery extends Query<PropertyInformationModel> {
  protected store: Cmg1003PropertyStore;

  dirtyChecker2: DirtyChecker2 = new DirtyChecker2(this.store, this);
  constructor() {
    const store = inject(Cmg1003PropertyStore);

    super(store);

    this.store = store;
  }

  private _assembleGiftsForSave(propertyInfo: PropertyInformationModel, currentBorrowerIds: number[]) {
    // Filter out any incomplete gifts.
    let validUiGifts: GiftModel[] = [];
    if (propertyInfo.giftsForUi) {
      validUiGifts = propertyInfo.giftsForUi?.filter((gift) => !!gift.borrowerId);
    }

    const otherAppsGifts = propertyInfo.gifts?.filter((gift) => !currentBorrowerIds.includes(gift.borrowerId));

    const giftsToDelete: GiftModel[] = [];

    validUiGifts?.forEach((validUiGift) => {
      const matchingOriginalGift = propertyInfo.gifts?.find(
        (originalGift) => originalGift.giftId === validUiGift.giftId && originalGift.borrowerId !== validUiGift.borrowerId,
      );

      if (matchingOriginalGift) {
        // Any gifts that are reassigned to another borrower must be created as a new gift - i.e. null gifts.
        validUiGift.giftId = null;

        // Then, the original gift must be marked for deletion.
        giftsToDelete.push({ ...matchingOriginalGift, isDeleted: true });
      }
    });

    return [...validUiGifts, ...otherAppsGifts, ...giftsToDelete];
  }

  /** Assembles and/or sanitizes PropertyInformation data to be ready for saving. */
  assemblePropertyForSave(currentBorrowerIds: number[]) {
    const propertyInfo = this.getValue();

    // Clear 1 accepts SSN as both a 9-digit (e.g. 333-22-4444) or a 4-digit (e.g. XXX-XX-4444) number.
    // The latter format requires sanitization to remove 'XXX-XX-'.
    const sanitizedTitleHolders = propertyInfo.titleHolders.map((titleHolder) => {
      return { ...titleHolder, ssn: titleHolder.ssn.replace('XXX-XX-', '') };
    });

    return {
      ...propertyInfo,
      gifts: this._assembleGiftsForSave(propertyInfo, currentBorrowerIds),
      titleHolders: sanitizedTitleHolders,
    };
  }

  setHead() {
    this.dirtyChecker2.dirtyChecker.setHead();
  }

  /**
   * Update the Property store and prevent it from being marked as dirty
   * @param updatedPropertyInfo
   */
  updatePropertyWithoutChangingDirtyState(updatedPropertyInfo: Partial<PropertyInformationModel>) {
    const updateStore = () => {
      this.store.update((state) => {
        return {
          ...state,
          ...updatedPropertyInfo,
        };
      });
    };
    // Only reset dirty state if the state was not previously dirty
    if (this.dirtyChecker2.dirtyChecker.isDirty()) {
      updateStore();
    } else {
      updateStore();
      this.setHead();
    }
  }
}
